import React, { useRef, useState } from 'react';
import CardMenu from './CardMenu';
import { fmtDay, fmtMoney } from '../lib/format';

// Three ways to look at the same leads, built from Julian's own Trello board.
//
// He works that board every day and asked for its shape here: cards he can
// push between columns, every button tucked behind a three-line menu at the
// top left so the card reads cleanly, and the choice of seeing the same jobs
// as a list or as small tiles when that suits him better.
//
// One card component serves all three. The alternative — a card per view —
// is three places to add the next button to, and the one that gets forgotten
// is always the one he is standing in a driveway using.

// The columns, in the order a job actually moves through them.
export const STAGES = [
  { key: 'new', label: 'New' },
  { key: 'estimate-sent', label: 'Estimate Sent' },
  { key: 'approved', label: 'Approved' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'completed', label: 'Completed' },
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'paid', label: 'Paid' },
];

// Which stages a drop can simply set, and which are the result of something
// happening. Approving emails the customer, assigning needs a technician
// picked, invoicing needs an invoice to exist — so those open the sheet that
// does the work, and the card only moves once it is done.
//
// 'paid' is in neither list: money either arrived or it did not, and a card
// dragged into Paid would be the board telling Julian he had been paid.
export const DROP_OPENS = ['estimate-sent', 'approved', 'assigned', 'invoiced'];
export const DROP_SETS = ['new', 'completed'];

const nameOf = (r) => r.homeownerId?.name || r.fullName || 'Unnamed';

export function LeadCard({ req, actions, onOpen, variant = 'board', dragProps }) {
  const amount = req.invoice?.amount ?? req.estimate?.amount;
  const where = [req.city, req.state].filter(Boolean).join(', ');

  return (
    <div
      // Spread first on purpose: className comes after, so nothing handed in
      // here can blow away the card's own class and leave it unstyled.
      {...(dragProps || {})}
      className={`lc lc-${variant}`}
      onClick={(e) => { if (!e.target.closest('button')) onOpen?.(req); }}
    >
      <div className="lc-top">
        {/* Top left, as asked. Everything this job can do lives in here, so
            the card itself stays a card rather than a wall of buttons. */}
        <CardMenu variant="bars" items={actions} label={`Actions for ${nameOf(req)}`} />
        <div className="lc-head">
          <div className="lc-name">{nameOf(req)}</div>
          {variant !== 'icon' && (
            <div className="lc-sub">
              {[req.serviceType, req.serviceAddress].filter(Boolean).join(' · ') || 'No details yet'}
            </div>
          )}
        </div>
      </div>

      {variant !== 'icon' && (
        <div className="lc-tags">
          {/* How ready this one is to buy, worked out the moment it landed
              (server/agents/qualify.js). First on the row because it is what
              decides which card gets rung first. */}
          {req.lead?.band && (
            <span
              className={`tag lead-${req.lead.band}`}
              title={[`${req.lead.score}/100`, ...(req.lead.signals || [])].join(' · ')}
            >
              {req.lead.band === 'hot' ? 'Hot lead' : req.lead.band === 'warm' ? 'Warm' : 'Cold'}
            </span>
          )}
          {req.urgency === 'Emergency' && <span className="tag tag-red">Emergency</span>}
          {req.estimateStatus === 'pending' && <span className="tag tag-amber">Awaiting approval</span>}
          {req.invoiceStatus === 'pending' && <span className="tag tag-amber">Invoice out</span>}
          {req.invoiceStatus === 'paid' && <span className="tag tag-green">Paid</span>}
          {req.assignedVendorId && (
            <span className="tag tag-teal">{req.assignedVendorId.businessName || 'Tech assigned'}</span>
          )}
          {!req.appointmentAt && <span className="tag tag-slate">No appointment</span>}
        </div>
      )}

      <div className="lc-foot">
        {amount != null ? <span className="lc-money">{fmtMoney(amount)}</span> : <span />}
        <span className="lc-date">{where || fmtDay(req.createdAt)}</span>
      </div>
    </div>
  );
}

// ── The board ──────────────────────────────────────────────────────────────
//
// Drag works with the browser's own drag and drop, which is the mouse. On a
// phone that API does nothing at all, so every card's menu also carries
// "Move to…". That is not a consolation prize: on a phone, picking the column
// off a list beats dragging a card across a screen you cannot see the end of.
export function LeadsBoard({ requests, actionsFor, onOpen, onMove }) {
  const [dragId, setDragId] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const dragged = useRef(null);

  const start = (req) => (e) => {
    dragged.current = req;
    setDragId(req._id);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox refuses to start a drag without something on the transfer.
    e.dataTransfer.setData('text/plain', req._id);
  };

  const drop = (stage) => (e) => {
    e.preventDefault();
    setOverKey(null);
    setDragId(null);
    const req = dragged.current;
    dragged.current = null;
    if (!req || req.status === stage.key) return;
    onMove?.(req, stage.key);
  };

  return (
    <div className="lb">
      {STAGES.map(stage => {
        const inStage = requests.filter(r => r.status === stage.key);
        const takesDrop = DROP_OPENS.includes(stage.key) || DROP_SETS.includes(stage.key);
        return (
          <section
            key={stage.key}
            className={`lb-col${overKey === stage.key ? ' over' : ''}${!takesDrop && dragId ? ' shut' : ''}`}
            onDragOver={(e) => { if (takesDrop) { e.preventDefault(); setOverKey(stage.key); } }}
            onDragLeave={() => setOverKey(k => (k === stage.key ? null : k))}
            onDrop={takesDrop ? drop(stage) : undefined}
          >
            <header className="lb-col-head">
              <span>{stage.label}</span>
              <span className="lb-count">{inStage.length}</span>
            </header>
            <div className="lb-col-body">
              {inStage.map(req => (
                <LeadCard
                  key={req._id}
                  req={req}
                  actions={actionsFor(req)}
                  onOpen={onOpen}
                  dragProps={{
                    draggable: true,
                    onDragStart: start(req),
                    onDragEnd: () => { setDragId(null); setOverKey(null); dragged.current = null; },
                  }}
                />
              ))}
              {inStage.length === 0 && (
                <div className="lb-empty">{takesDrop ? 'Drop a job here' : 'Nothing here'}</div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ── The switcher ───────────────────────────────────────────────────────────
export function ViewSwitch({ view, setView }) {
  const views = [
    { key: 'board', label: 'Board', icon: <><rect x="3" y="4" width="5" height="16" rx="1" /><rect x="10" y="4" width="5" height="11" rx="1" /><rect x="17" y="4" width="4" height="7" rx="1" /></> },
    { key: 'list', label: 'List', icon: <><path d="M4 6h16M4 12h16M4 18h16" /></> },
    { key: 'icons', label: 'Tiles', icon: <><rect x="4" y="4" width="7" height="7" rx="1" /><rect x="13" y="4" width="7" height="7" rx="1" /><rect x="4" y="13" width="7" height="7" rx="1" /><rect x="13" y="13" width="7" height="7" rx="1" /></> },
  ];
  return (
    <div className="vswitch" role="group" aria-label="How to show the leads">
      {views.map(v => (
        <button
          key={v.key}
          type="button"
          className={`vswitch-btn${view === v.key ? ' on' : ''}`}
          aria-pressed={view === v.key}
          onClick={() => setView(v.key)}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            {v.icon}
          </svg>
          <span>{v.label}</span>
        </button>
      ))}
    </div>
  );
}
